import React, { useCallback } from "react";
import { UndoOutlined } from "@ant-design/icons";
import { Row, Col, InputNumber, Form, Select, Input, Spin, Alert, Button, Card } from "antd";
import { STATUS, QuoteFeedbackTypeMap, QuoteFeedbackType, PricingEngineResultStatusMap } from "@/utils/enum";
import { Validator } from "@/utils/validator";
import AlertWithBorder from "@/components/AlertWithBorder";
import usePricingEngine from "./usePricingEngine";

export default function VehiclePricing({ formRef, shipmentData }) {
  const onPricingSuccess = useCallback(
    pricingData => {
      formRef?.setFieldsValue({
        dealerPrice: pricingData.dealerPrice,
        transporterPrice: pricingData.transporterPrice,
      });
    },
    [formRef],
  );

  const { data, status, error } = usePricingEngine({ shipmentData, onSuccess: onPricingSuccess });

  const alertMessage = PricingEngineResultStatusMap[data?.type]?.message;
  const alertType = PricingEngineResultStatusMap[data?.type]?.type;

  const handleReset = () => {
    formRef?.setFieldsValue({
      transporterPrice: data?.transporterPrice,
      dealerPrice: data?.dealerPrice,
      transporterPriceFeedbackType: undefined,
      dealerPriceFeedbackType: undefined,
      transporterOtherFeedbackNote: undefined,
      dealerOtherFeedbackNote: undefined,
    });
  };

  return (
    <Card
      title="Recommended Pricing"
      className="noLeftMarginCard"
      extra={
        <Button icon={<UndoOutlined />} onClick={handleReset}>
          Reset
        </Button>
      }
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {status !== STATUS.IDLE && (
            <Spin spinning={status === STATUS.LOADING}>
              <AlertWithBorder
                key={data?.id}
                message={alertMessage}
                type={alertType}
                actionButtons={[
                  {
                    label: "Review",
                    key: "review-pricing-engine-results",
                    href: `/admin/shipments/view/messages?id=${data?.id}&tab=${alertType}&shipmentId=${shipmentData?.id}`,
                  },
                ]}
                style={{ marginBottom: 16 }}
              />
            </Spin>
          )}
        </Col>

        {status === STATUS.ERROR && (
          <Col span={24}>
            <Alert
              message="Pricing Engine Error"
              description={error?.message || "Failed to load recommended pricing. Please try again."}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          </Col>
        )}

        {status === STATUS.SUCCESS && (
          <Col span={24}>
            <Row gutter={[16, 16]}>
              {/* Dealer Price */}
              <Col xs={12} sm={12} md={12} lg={12}>
                <Form.Item
                  name="dealerPrice"
                  label="Dealer Price"
                  rules={[
                    Validator.required("Dealer Price is required"),
                    Validator.custom(value => value > 0, "Dealer Price must be greater than 0"),
                  ]}
                >
                  <InputNumber
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={value => value.replace(/\$\s?|(,*)/g, "")}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>

              {/* Transporter Price */}
              <Col xs={12} sm={12} md={12} lg={12}>
                <Form.Item
                  name="transporterPrice"
                  label="Transporter Price"
                  rules={[
                    Validator.required("Transporter Price is required"),
                    Validator.custom(value => value > 0, "Transporter Price must be greater than 0"),
                  ]}
                >
                  <InputNumber
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={value => value.replace(/\$\s?|(,*)/g, "")}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Main Form.Item to control feedback section visibility */}
            <Form.Item
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.transporterPrice !== currentValues.transporterPrice ||
                prevValues.dealerPrice !== currentValues.dealerPrice
              }
              noStyle
            >
              {({ getFieldValue }) => {
                const currentTransporterPrice = getFieldValue("transporterPrice");
                const currentDealerPrice = getFieldValue("dealerPrice");
                const showTransporterFeedback = currentTransporterPrice !== data?.transporterPrice;
                const showDealerFeedback = currentDealerPrice !== data?.dealerPrice;
                const showFeedbackSection = showTransporterFeedback || showDealerFeedback;

                if (!showFeedbackSection) return null;

                return (
                  <Row gutter={[16, 16]}>
                    {/* Left Column - Dealer Feedback */}
                    <Col xs={12} sm={12} md={12} lg={12}>
                      {showDealerFeedback && (
                        <>
                          <Form.Item
                            name="dealerPriceFeedbackType"
                            label="Dealer Feedback"
                            rules={[Validator.required("Dealer Feedback is required")]}
                          >
                            <Select style={{ width: "100%" }}>
                              {Object.keys(QuoteFeedbackTypeMap).map(key => (
                                <Select.Option key={key} value={parseInt(key, 10)}>
                                  {QuoteFeedbackTypeMap[key]}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item
                            noStyle
                            shouldUpdate={prevValues =>
                              prevValues.dealerPriceFeedbackType !== getFieldValue("dealerPriceFeedbackType")
                            }
                          >
                            {() => {
                              const feedbackType = getFieldValue("dealerPriceFeedbackType");

                              if (feedbackType !== QuoteFeedbackType.Other) return null;

                              return (
                                <Form.Item
                                  name="dealerOtherFeedbackNote"
                                  label="Notes"
                                  rules={[Validator.required("Please provide feedback notes")]}
                                >
                                  <Input.TextArea rows={4} />
                                </Form.Item>
                              );
                            }}
                          </Form.Item>
                        </>
                      )}
                    </Col>

                    {/* Right Column - Transporter Feedback */}
                    <Col xs={12} sm={12} md={12} lg={12}>
                      {showTransporterFeedback && (
                        <>
                          <Form.Item
                            name="transporterPriceFeedbackType"
                            label="Transporter Feedback"
                            rules={[Validator.required("Transporter Feedback is required")]}
                          >
                            <Select style={{ width: "100%" }}>
                              {Object.keys(QuoteFeedbackTypeMap).map(key => (
                                <Select.Option key={key} value={parseInt(key, 10)}>
                                  {QuoteFeedbackTypeMap[key]}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item
                            noStyle
                            shouldUpdate={prevValues =>
                              prevValues.transporterPriceFeedbackType !== getFieldValue("transporterPriceFeedbackType")
                            }
                          >
                            {() => {
                              const feedbackType = getFieldValue("transporterPriceFeedbackType");

                              if (feedbackType !== QuoteFeedbackType.Other) return null;

                              return (
                                <Form.Item
                                  name="transporterOtherFeedbackNote"
                                  label="Notes"
                                  rules={[Validator.required("Please provide feedback notes")]}
                                >
                                  <Input.TextArea rows={4} />
                                </Form.Item>
                              );
                            }}
                          </Form.Item>
                        </>
                      )}
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>
          </Col>
        )}
      </Row>
    </Card>
  );
}
